'use client'

import { Fragment, type KeyboardEvent, type ReactNode, useState } from 'react'

import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { VideoPlayerModal } from '@src/components/VideoPlayer/VideoPlayerModal'
import { type UseClickActionArgs } from '@src/hooks/useClickAction/useClickActionArgs'
import { useClickActionContext } from '@src/hooks/useClickAction/useClickActionContext'
import { useOnClick } from '@src/hooks/useClickAction/useOnClick'
import { filterBuilderAttributes, getBuilderPreviewMode } from '@src/utils/builder.util'
import { EXPERIMENTS } from '@src/utils/constants.util'
import { useExperimentContext } from '@src/utils/experimentProvider'

export const useClickAction = <T extends UseClickActionArgs = UseClickActionArgs>(args: T) => {
  const { actionName = 'none', popoutVideo, navigate, attributes } = args
  const videoId = popoutVideo?.videoId
  const { edit } = getBuilderPreviewMode()
  const [isLoading, setIsLoading] = useState(false)
  const [isVideoOpen, setVideoOpen] = useState(false)
  const { isLoadingAnyAction } = useClickActionContext()

  const shouldSkipOnClick = edit || isLoading || isLoadingAnyAction || actionName === 'none'

  const { onClick } = useOnClick({
    setVideoOpen,
    setIsLoading,
    shouldSkip: shouldSkipOnClick,
    useClickActionArgs: args,
  })

  const { allVariants } = useExperimentContext() ?? {}
  const cartFlyoutTest = allVariants?.[EXPERIMENTS.cartFlyOut]?.value === 'variant'

  const onKeyDown = async (e: KeyboardEvent) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      await onClick?.()
    }
  }

  const isWrapping = actionName === 'navigate'

  const renderVideo = () => {
    return (
      <>
        {videoId && (
          <VideoPlayerModal
            autoplay
            isOpen={isVideoOpen}
            onClose={() => {
              setVideoOpen(false)
            }}
            videoId={videoId}
          />
        )}
      </>
    )
  }

  const ClickWrapper = ({ children }: { children: ReactNode }) => {
    const usingCartFlyout = cartFlyoutTest && navigate?.href === '/cart'
    const wrapperProps =
      !isWrapping || usingCartFlyout
        ? {}
        : {
            ...(filterBuilderAttributes(attributes) as any),
            href: String(navigate?.href),
            target: navigate?.target,
            onClick,
            onKeyDown,
          }

    const Wrapper = isWrapping && !usingCartFlyout ? LocalizedLink : Fragment
    return (
      <Wrapper {...wrapperProps}>
        {children}
        {renderVideo()}
      </Wrapper>
    )
  }

  const handlers = shouldSkipOnClick
    ? {}
    : {
        onClick,
        onKeyDown,
      }

  return {
    isWrapping,
    handlers,
    isLoading,
    ClickWrapper,
    renderVideo,
  }
}
